import { computed, reactive, ref, watch } from "vue";
import { $dt } from "@primevue/themes";

const layoutConfig = reactive({
  preset: "Aura",
  primary: "red",
  surface: null,
  themeMode: "system", // "light", "dark", or "system"
  menuMode: "static",
  menuTheme: "colorScheme",
});

const layoutState = reactive({
  staticMenuDesktopInactive: false,
  overlayMenuActive: false,
  sidebarActive: false,
  anchored: false,
  overlaySubmenuActive: false,
  profileSidebarVisible: false,
  configSidebarVisible: false,
  staticMenuMobileActive: false,
  menuHoverActive: false,
  activeMenuItem: null,
});

const outsideClickListener = ref(null);

export function useLayout() {
  const prefersDarkScheme = ref(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  // Watch for system color scheme changes
  watch(prefersDarkScheme, () => {
    if (layoutConfig.themeMode === "system") {
      applyTheme();
    }
  });
  const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaQuery.addEventListener("change", (e) => {
    prefersDarkScheme.value = e.matches;
  });

  const setPrimary = (value) => {
    layoutConfig.primary = value;
  };

  const setSurface = (value) => {
    layoutConfig.surface = value;
  };

  const setPreset = (value) => {
    layoutConfig.preset = value;
  };

  const setMenuMode = (mode) => {
    layoutConfig.menuMode = mode;

    if (layoutConfig.menuMode === "static") {
      layoutState.staticMenuDesktopInactive = false;
    }
  };

  const setMenuTheme = (value) => {
    layoutConfig.menuTheme = value;
  };

  const setThemeMode = (value) => {
    layoutConfig.themeMode = value;

    if (!document.startViewTransition) {
      applyTheme();
      return;
    }

    document.startViewTransition(() => applyTheme());
  };

  const isDarkMode = computed(
    () =>
      layoutConfig.themeMode === "dark" ||
      (layoutConfig.themeMode === "system" && prefersDarkScheme.value),
  );

  const applyTheme = () => {
    if (isDarkMode.value) {
      document.documentElement.classList.add("app-dark");
    } else {
      document.documentElement.classList.remove("app-dark");
    }
  };

  applyTheme();

  const setActiveMenuItem = (item) => {
    layoutState.activeMenuItem = item.value || item;
  };

  const setMenuStates = (value) => {
    layoutState.overlaySubmenuActive = value;
    layoutState.menuHoverActive = value;
  };

  const setStaticMenuMobile = () => {
    layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
  };

  const watchSidebarActive = () => {
    watch(isSidebarActive, (newVal) => {
      if (newVal) {
        bindOutsideClickListener();
      } else {
        unbindOutsideClickListener();
      }
    });
  };

  const onMenuToggle = () => {
    if (layoutConfig.menuMode === "overlay") {
      layoutState.overlayMenuActive = !layoutState.overlayMenuActive;
    }

    if (window.innerWidth > 991) {
      layoutState.staticMenuDesktopInactive =
        !layoutState.staticMenuDesktopInactive;
    } else {
      layoutState.staticMenuMobileActive = !layoutState.staticMenuMobileActive;
    }
  };

  const onProfileSidebarToggle = () => {
    layoutState.profileSidebarVisible = !layoutState.profileSidebarVisible;
  };

  const onConfigSidebarToggle = () => {
    if (isSidebarActive.value) {
      resetMenu();
      unbindOutsideClickListener();
    }

    layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
  };

  const onSidebarToggle = (value) => {
    layoutState.sidebarActive = value;
  };

  const onAnchorToggle = () => {
    layoutState.anchored = !layoutState.anchored;
  };

  const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
      outsideClickListener.value = (event) => {
        if (isOutsideClicked(event)) {
          resetMenu();
        }
      };
      document.addEventListener("click", outsideClickListener.value);
    }
  };

  const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
      document.removeEventListener("click", outsideClickListener.value);
      outsideClickListener.value = null;
    }
  };

  const isOutsideClicked = (event) => {
    const sidebarEl = document.querySelector(".layout-sidebar");
    const topbarButtonEl = document.querySelector(".topbar-menubutton");

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  };

  const resetMenu = () => {
    layoutState.overlayMenuActive = false;
    layoutState.overlaySubmenuActive = false;
    layoutState.staticMenuMobileActive = false;
    layoutState.menuHoverActive = false;
    layoutState.configSidebarVisible = false;
  };

  const isSidebarActive = computed(
    () =>
      layoutState.overlayMenuActive ||
      layoutState.staticMenuMobileActive ||
      layoutState.overlaySubmenuActive,
  );

  const neenaDarkgreen = $dt("neena.darkgreen.500").value;
  const stone300 = $dt("stone.300").value;

  const iconFillColor = computed(() => {
    return isDarkMode.value ? stone300 : neenaDarkgreen;
  });

  const isDesktop = computed(() => window.innerWidth > 991);
  const isSlim = computed(() => layoutConfig.menuMode === "slim");
  const isSlimPlus = computed(() => layoutConfig.menuMode === "slim-plus");
  const isHorizontal = computed(() => layoutConfig.menuMode === "horizontal");

  const getPrimary = computed(() => layoutConfig.primary);
  const getSurface = computed(() => layoutConfig.surface);
  const getThemeMode = computed(() => layoutConfig.themeMode);

  const sm = 576;
  const md = 768;
  const lg = 992;
  const xl = 1200;
  const xxl = 1920;

  return {
    layoutConfig,
    layoutState,
    getPrimary,
    getSurface,
    getThemeMode,
    setPrimary,
    setSurface,
    setPreset,
    setMenuMode,
    setMenuTheme,
    setThemeMode,
    onMenuToggle,
    setMenuStates,
    setStaticMenuMobile,
    watchSidebarActive,
    isSidebarActive,
    setActiveMenuItem,
    onProfileSidebarToggle,
    onConfigSidebarToggle,
    onSidebarToggle,
    onAnchorToggle,
    isDarkMode,
    isSlim,
    isSlimPlus,
    isHorizontal,
    isDesktop,
    unbindOutsideClickListener,
    sm,
    md,
    lg,
    xl,
    xxl,
    iconFillColor,
  };
}
